import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { environment } from '../../../environment/environment';
import { AuthService } from '../../../services/auth/auth.service';

interface NotifyUserSummary {
  user_id: number;
  email: string;
  name: string;
}

interface OrgUserCandidate {
  user_id: number;
  name: string;
  email: string;
}

interface StockEditRow {
  location_id: number;
  name: string;
  quantity: number;
}

interface MaterialItem {
  material_id: number;
  organization_id: number;
  name: string;
  description: string | null;
  total_quantity: number;
  deleted: boolean;
  created_at?: string;
  updated_at?: string;
}

interface EventRequestItem {
  event_id: number;
  event_guid: string;
  event_short_name: string | null;
  start_date: string;
  end_date: string;
  event_org_id?: number;
  event_org_name?: string | null;
  requests: {
    event_material_id: number;
    material_id: number;
    material_name: string;
    quantity: number;
    status: string;
    admin_notes: string | null;
    instructor_notes: string | null;
    request_created?: string;
    remaining_for_event?: number;
  }[];
}

interface AvailabilityBooking {
  event_material_id: number;
  material_id: number;
  material_name: string;
  quantity: number;
  status: string;
  event_guid: string;
  event_short_name: string | null;
  start_date: string;
  end_date: string;
  event_org_id: number;
}

interface MaterialLocationRow {
  location_id: number;
  name: string;
  sort_order: number;
  stock_quantity?: number;
  can_delete?: boolean;
}

@Component({
  selector: 'app-duna-materials-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatExpansionModule,
    MatSelectModule,
  ],
  templateUrl: './duna-materials-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-materials-overview.component.scss'],
})
export class DunaMaterialsOverviewComponent implements OnInit {
  materials: MaterialItem[] = [];
  eventRequests: EventRequestItem[] = [];
  loadingMaterials = false;
  loadingRequests = false;
  saving = false;
  errorMessage: string | null = null;
  pdfLoading = false;
  accessDenied = false;

  showCreateMaterial = false;
  editingMaterialId: number | null = null;
  createForm = { name: '', total_quantity: 1, description: '' };
  editForm = { name: '', total_quantity: 1, description: '' };

  adminNotes: Record<number, string> = {};

  calendarFrom = '';
  calendarTo = '';
  calendarMaterialId: number | null = null;
  calendarBookings: AvailabilityBooking[] = [];
  calendarLoading = false;
  showCalendar = true;

  materialLocations: MaterialLocationRow[] = [];
  loadingLocations = false;
  newLocationName = '';
  editingLocationId: number | null = null;
  editLocationName = '';
  multiLocationEnabled = false;
  editStockRows: StockEditRow[] = [];

  orgUserCandidates: OrgUserCandidate[] = [];
  notifyAllUserIds: number[] = [];
  notifyByLocationIds: Record<number, number[]> = {};
  loadingNotify = false;
  savingNotify = false;
  loadingOrgUsers = false;
  orgUsersLoadError: string | null = null;
  /** Shared search while a notify user mat-select panel is open. */
  notifyUserSearch = '';
  private notifySetupLoaded = false;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    this.calendarFrom = start.toISOString().slice(0, 10);
    this.calendarTo = end.toISOString().slice(0, 10);
    this.loadMaterials();
    this.loadRequests();
    this.loadOrgMaterialsSettings();
    this.loadLocations();
  }

  private resolveOrgId(): number | null {
    const user = this.auth.getCurrentUser() as Record<string, unknown> | null;
    const raw =
      user?.['organizations_id'] ??
      (user?.['user_info'] as Record<string, unknown> | undefined)?.[
        'organizations_id'
      ];
    const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  loadOrgMaterialsSettings(): void {
    const orgId = this.resolveOrgId();
    if (orgId == null) return;
    this.http
      .get<{ data: Record<string, unknown> }>(
        `${environment.apiUrl}/organizations/${orgId}/settings`
      )
      .subscribe({
        next: (res) => {
          const data = res.data || {};
          this.multiLocationEnabled =
            data['materials_multi_location_enabled'] === true ||
            data['materials_multi_location_enabled'] === 'true';
          this.loadNotifySetupIfNeeded();
          this.cdr.detectChanges();
        },
        error: () => {},
      });
  }

  /** Avoid mat-select ngModel loop: never return a fresh [] from the template. */
  private ensureNotifyKeysForLocations(): void {
    for (const loc of this.materialLocations) {
      if (!Object.prototype.hasOwnProperty.call(this.notifyByLocationIds, loc.location_id)) {
        this.notifyByLocationIds[loc.location_id] = [];
      }
    }
  }

  private loadNotifySetupIfNeeded(): void {
    if (this.notifySetupLoaded) {
      return;
    }
    if (!this.multiLocationEnabled && this.materialLocations.length === 0) {
      return;
    }
    this.notifySetupLoaded = true;
    this.loadNotifyRecipients();
  }

  /** Older API without `candidates` on notify-recipients. */
  private loadOrgUserCandidatesFallback(): void {
    this.http
      .get<{
        data?: {
          id: number;
          first_name?: string;
          last_name?: string;
          user?: string;
          email?: string;
        }[];
        items?: {
          id: number;
          first_name?: string;
          last_name?: string;
          user?: string;
          email?: string;
        }[];
      }>(`${environment.apiUrl}/users/manage`, {
        params: { page: 1, pageSize: 500 },
      })
      .subscribe({
        next: (res) => {
          const rows = res.data ?? res.items ?? [];
          this.orgUserCandidates = rows.map((u) => ({
            user_id: u.id,
            email: u.email || u.user || '',
            name:
              `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
              u.user ||
              `Bruger ${u.id}`,
          }));
          this.orgUsersLoadError =
            this.orgUserCandidates.length > 0
              ? null
              : 'Ingen brugere fundet i organisationen.';
          this.loadingOrgUsers = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.orgUserCandidates = [];
          this.orgUsersLoadError =
            err?.error?.message ||
            'Kunne ikke hente brugere til notifikation.';
          this.loadingOrgUsers = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadNotifyRecipients(): void {
    this.loadingNotify = true;
    this.loadingOrgUsers = true;
    this.orgUsersLoadError = null;
    this.http
      .get<{
        all: NotifyUserSummary[];
        by_location: Record<string, NotifyUserSummary[]>;
        candidates?: OrgUserCandidate[];
      }>(`${environment.apiUrl}/materials/notify-recipients`)
      .subscribe({
        next: (res) => {
          this.notifyAllUserIds = (res.all || []).map((u) => u.user_id);
          this.notifyByLocationIds = {};
          const byLoc = res.by_location || {};
          for (const key of Object.keys(byLoc)) {
            const locId = parseInt(key, 10);
            if (!Number.isFinite(locId)) continue;
            this.notifyByLocationIds[locId] = (byLoc[key] || []).map(
              (u) => u.user_id
            );
          }
          this.ensureNotifyKeysForLocations();
          this.loadingNotify = false;
          if (res.candidates !== undefined) {
            this.orgUserCandidates = res.candidates;
            this.orgUsersLoadError =
              this.orgUserCandidates.length > 0
                ? null
                : 'Ingen brugere fundet i organisationen.';
            this.loadingOrgUsers = false;
          } else {
            this.loadOrgUserCandidatesFallback();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingNotify = false;
          this.loadingOrgUsers = false;
          this.cdr.detectChanges();
        },
      });
  }

  filteredNotifyUserCandidates(): OrgUserCandidate[] {
    const q = this.notifyUserSearch.trim().toLowerCase();
    if (!q) {
      return this.orgUserCandidates;
    }
    return this.orgUserCandidates.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        String(u.user_id).includes(q)
      );
    });
  }

  /** Closed mat-select label so the field stays readable with many selections. */
  notifySelectedLabel(userIds: number[] | undefined): string {
    const ids = userIds ?? [];
    if (ids.length === 0) {
      return 'Ingen valgt — klik for at vælge';
    }
    if (ids.length === 1) {
      const u = this.orgUserCandidates.find((c) => c.user_id === ids[0]);
      return u ? u.name : '1 person valgt';
    }
    const names = ids
      .map((id) => this.orgUserCandidates.find((c) => c.user_id === id)?.name)
      .filter((n): n is string => !!n);
    if (names.length <= 2) {
      return names.join(', ');
    }
    return `${ids.length} personer valgt`;
  }

  saveNotifyRecipients(): void {
    this.savingNotify = true;
    const by_location: Record<string, number[]> = {};
    for (const key of Object.keys(this.notifyByLocationIds)) {
      by_location[key] = this.notifyByLocationIds[parseInt(key, 10)] || [];
    }
    this.http
      .put(`${environment.apiUrl}/materials/notify-recipients`, {
        all: this.notifyAllUserIds,
        by_location,
      })
      .subscribe({
        next: () => {
          this.savingNotify = false;
          this.loadNotifyRecipients();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke gemme notifikationsmodtagere';
          this.savingNotify = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadMaterials(): void {
    this.loadingMaterials = true;
    this.errorMessage = null;
    this.http
      .get<{ items: MaterialItem[] }>(`${environment.apiUrl}/materials`)
      .subscribe({
        next: (res) => {
          this.materials = (res.items || []).filter((m) => !m.deleted);
          this.loadingMaterials = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          if (err?.status === 403) {
            this.accessDenied = true;
            this.errorMessage =
              err?.error?.message || 'Materielmodulet er ikke aktiveret';
          } else {
            this.errorMessage =
              err?.error?.message || 'Kunne ikke hente materialer';
          }
          this.loadingMaterials = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadRequests(): void {
    this.loadingRequests = true;
    this.http
      .get<{ items: EventRequestItem[] }>(
        `${environment.apiUrl}/materials/requests`
      )
      .subscribe({
        next: (res) => {
          this.eventRequests = res.items || [];
          this.adminNotes = {};
          this.eventRequests.forEach((ev) => {
            ev.requests.forEach((r) => {
              if (r.admin_notes)
                this.adminNotes[r.event_material_id] = r.admin_notes;
            });
          });
          this.loadingRequests = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          if (err?.status === 403) {
            this.accessDenied = true;
          }
          this.loadingRequests = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadCalendar(): void {
    if (!this.calendarFrom || !this.calendarTo) return;
    this.calendarLoading = true;
    let url = `${environment.apiUrl}/materials/availability?from=${encodeURIComponent(this.calendarFrom)}&to=${encodeURIComponent(this.calendarTo)}`;
    if (this.calendarMaterialId) {
      url += `&material_id=${this.calendarMaterialId}`;
    }
    this.http
      .get<{ bookings: AvailabilityBooking[] }>(url)
      .subscribe({
        next: (res) => {
          this.calendarBookings = res.bookings || [];
          this.calendarLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.calendarLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadLocations(): void {
    this.loadingLocations = true;
    this.http
      .get<{ items: MaterialLocationRow[] }>(
        `${environment.apiUrl}/materials/locations`
      )
      .subscribe({
        next: (res) => {
          this.materialLocations = res.items || [];
          this.ensureNotifyKeysForLocations();
          this.loadNotifySetupIfNeeded();
          this.loadingLocations = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingLocations = false;
          this.cdr.detectChanges();
        },
      });
  }

  createLocation(): void {
    const name = this.newLocationName.trim();
    if (!name) return;
    this.saving = true;
    this.http
      .post(`${environment.apiUrl}/materials/locations`, { name })
      .subscribe({
        next: () => {
          this.newLocationName = '';
          this.saving = false;
          this.loadLocations();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke oprette lokation';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  startEditLocation(loc: MaterialLocationRow): void {
    this.editingLocationId = loc.location_id;
    this.editLocationName = loc.name;
  }

  cancelEditLocation(): void {
    this.editingLocationId = null;
    this.editLocationName = '';
  }

  saveEditLocation(): void {
    if (this.editingLocationId == null) return;
    const name = this.editLocationName.trim();
    if (!name) {
      this.errorMessage = 'Lokationsnavn kan ikke være tom.';
      return;
    }
    this.saving = true;
    this.http
      .patch(`${environment.apiUrl}/materials/locations/${this.editingLocationId}`, {
        name,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.cancelEditLocation();
          this.loadLocations();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke opdatere lokation';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  canDeleteLocation(loc: MaterialLocationRow): boolean {
    if (loc.can_delete === false) return false;
    return (loc.stock_quantity ?? 0) <= 0;
  }

  deleteLocation(loc: MaterialLocationRow): void {
    if (!this.canDeleteLocation(loc)) {
      this.errorMessage =
        'Lokationen kan kun slettes når der ikke er materiel på lageret.';
      return;
    }
    if (
      !confirm(
        `Slet lagerlokationen "${loc.name}"? Dette kan ikke fortrydes.`
      )
    ) {
      return;
    }
    this.saving = true;
    this.http
      .delete(`${environment.apiUrl}/materials/locations/${loc.location_id}`)
      .subscribe({
        next: () => {
          delete this.notifyByLocationIds[loc.location_id];
          this.saving = false;
          this.loadLocations();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke slette lokation';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  openCreateMaterial(): void {
    this.showCreateMaterial = true;
    this.createForm = { name: '', total_quantity: 1, description: '' };
  }

  cancelCreateMaterial(): void {
    this.showCreateMaterial = false;
  }

  saveCreateMaterial(): void {
    if (!this.createForm.name.trim()) return;
    this.saving = true;
    this.http
      .post<{ material_id: number }>(`${environment.apiUrl}/materials`, {
        name: this.createForm.name.trim(),
        total_quantity: Math.max(1, this.createForm.total_quantity),
        description: this.createForm.description.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.showCreateMaterial = false;
          this.loadMaterials();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke oprette materiale';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  openEditMaterial(m: MaterialItem): void {
    this.editingMaterialId = m.material_id;
    this.editForm = {
      name: m.name,
      total_quantity: m.total_quantity,
      description: m.description || '',
    };
    this.editStockRows = [];
    if (this.multiLocationEnabled && this.materialLocations.length) {
      this.http
        .get<{ items: { location_id: number; name: string; quantity: number }[] }>(
          `${environment.apiUrl}/materials/${m.material_id}/stock`
        )
        .subscribe({
          next: (res) => {
            const stockMap = new Map(
              (res.items || []).map((r) => [r.location_id, r.quantity])
            );
            this.editStockRows = this.materialLocations.map((loc) => ({
              location_id: loc.location_id,
              name: loc.name,
              quantity: stockMap.get(loc.location_id) ?? 0,
            }));
            this.cdr.detectChanges();
          },
          error: () => {
            this.editStockRows = this.materialLocations.map((loc) => ({
              location_id: loc.location_id,
              name: loc.name,
              quantity: 0,
            }));
            this.cdr.detectChanges();
          },
        });
    }
  }

  stockSum(): number {
    return this.editStockRows.reduce((s, r) => s + Math.max(0, r.quantity), 0);
  }

  cancelEditMaterial(): void {
    this.editingMaterialId = null;
    this.editStockRows = [];
  }

  saveEditMaterial(): void {
    if (this.editingMaterialId == null || !this.editForm.name.trim()) return;
    this.saving = true;
    const materialId = this.editingMaterialId;
    const totalQty =
      this.multiLocationEnabled && this.editStockRows.length
        ? this.stockSum()
        : Math.max(1, this.editForm.total_quantity);
    this.http
      .patch(`${environment.apiUrl}/materials/${materialId}`, {
        name: this.editForm.name.trim(),
        total_quantity: Math.max(1, totalQty),
        description: (this.editForm.description || '').trim() || null,
      })
      .subscribe({
        next: () => {
          if (this.multiLocationEnabled && this.editStockRows.length) {
            this.http
              .put(`${environment.apiUrl}/materials/${materialId}/stock`, {
                items: this.editStockRows.map((r) => ({
                  location_id: r.location_id,
                  quantity: Math.max(0, r.quantity),
                })),
              })
              .subscribe({
                next: () => this.finishSaveEdit(),
                error: (err) => {
                  this.errorMessage =
                    err?.error?.message || 'Kunne ikke gemme lager pr. lokation';
                  this.saving = false;
                  this.cdr.detectChanges();
                },
              });
          } else {
            this.finishSaveEdit();
          }
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke opdatere materiale';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  private finishSaveEdit(): void {
    this.saving = false;
    this.editingMaterialId = null;
    this.editStockRows = [];
    this.loadMaterials();
    this.cdr.detectChanges();
  }

  deleteMaterial(m: MaterialItem): void {
    if (!confirm('Slet materialet "' + m.name + '"?')) return;
    this.saving = true;
    this.http
      .delete(`${environment.apiUrl}/materials/${m.material_id}`)
      .subscribe({
        next: () => {
          this.saving = false;
          this.loadMaterials();
          this.loadRequests();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage =
            err?.error?.message || 'Kunne ikke slette materiale';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  approveRequest(eventGuid: string, eventMaterialId: number): void {
    this.saving = true;
    this.http
      .post(
        `${environment.apiUrl}/events/${eventGuid}/materials/${eventMaterialId}/approve`,
        {}
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.loadRequests();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Kunne ikke godkende';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  rejectRequest(eventGuid: string, eventMaterialId: number): void {
    this.saving = true;
    this.http
      .patch(
        `${environment.apiUrl}/events/${eventGuid}/materials/${eventMaterialId}`,
        { status: 'rejected' }
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.loadRequests();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Kunne ikke afvise';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  saveAdminNotes(
    eventGuid: string,
    eventMaterialId: number,
    notes: string
  ): void {
    this.http
      .patch(
        `${environment.apiUrl}/events/${eventGuid}/materials/${eventMaterialId}`,
        { admin_notes: notes }
      )
      .subscribe({
        next: () => this.loadRequests(),
        error: () => {},
      });
  }

  statusLabel(s: string): string {
    const map: Record<string, string> = {
      pending: 'Afventer',
      approved: 'Godkendt',
      rejected: 'Afvist',
    };
    return map[s] || s;
  }

  exportPdf(): void {
    this.pdfLoading = true;
    this.http
      .post<{
        pdf_content?: string;
        filename?: string;
      }>(`${environment.apiUrl}/reports/materials-readiness`, {
        status: 'approved',
      })
      .subscribe({
        next: (res) => {
          this.pdfLoading = false;
          this.cdr.detectChanges();
          if (res.pdf_content && res.filename) {
            const binaryString = atob(res.pdf_content);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = res.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        },
        error: () => {
          this.pdfLoading = false;
          this.cdr.detectChanges();
        },
      });
  }
}
